import { z } from "zod";
import { connectorIntegrationSchema, slackConnectionCapabilitySchema, } from "./connectors.js";
const nonEmptyString = z.string().trim().min(1);
const metadataSchema = z.record(z.string(), z.unknown()).default({});
const secretValuesSchema = z.record(nonEmptyString, z.string().min(1));
export const setRuntimePluginToggleRequestSchema = z.object({
    pluginSlug: nonEmptyString.max(120),
    enabled: z.boolean(),
}).strict();
export const setRuntimePluginToggleResponseSchema = z.object({
    toggle: z.object({
        pluginSlug: nonEmptyString,
        enabled: z.boolean(),
    }).strict(),
}).strict();
export const runtimePluginNativeOAuthAppSchema = z.object({
    providerSlug: nonEmptyString,
    configured: z.literal(true),
    redirectUri: z.string().url().optional(),
    scopes: z.array(nonEmptyString),
    metadata: z.record(z.string(), z.unknown()),
    credentialVersion: z.number().int().nonnegative(),
}).strict();
export const startNotionCliAuthResponseSchema = z.object({
    sessionId: nonEmptyString,
    verificationUrl: z.string().url(),
    verificationCode: z.string().regex(/^[A-Z0-9]+-[A-Z0-9]+$/u),
    expiresAt: z.number().nonnegative(),
}).strict();
export const pollNotionCliAuthRequestSchema = z.object({
    sessionId: z.string().trim().min(20).max(100),
}).strict();
export const pollNotionCliAuthResponseSchema = z.object({
    ok: z.literal(true),
}).strict();
const slackOutboundEndpointSchema = z.object({
    organisationId: nonEmptyString,
    externalTeamId: z.string().regex(/^T[A-Z0-9]+$/u),
    externalUserId: z.string().regex(/^U[A-Z0-9]+$/u),
    botToken: z.string().min(1),
    botScope: z.string().min(1),
}).strict();
const sourceConnectorSchema = z.object({
    organisationId: nonEmptyString,
    connectorProvider: z.enum(["slack", "outlook_mail"]),
    enabledEntities: z.array(nonEmptyString).default(["mention", "direct_message"]),
    config: metadataSchema,
    secrets: secretValuesSchema,
}).strict();
const googleSourceConnectorSchema = z.object({
    organisationId: nonEmptyString,
    connectorProvider: z.enum(["gmail", "google_calendar", "google_drive", "google_sheets"]),
    enabledEntities: z.array(nonEmptyString).min(1),
    config: metadataSchema,
    secrets: secretValuesSchema,
}).strict();
export const nativeOAuthHandoffSchema = z.discriminatedUnion("kind", [
    z.object({
        providerSlug: z.literal("slack"),
        kind: z.literal("slack-capabilities"),
        capabilities: z.array(slackConnectionCapabilitySchema).min(1),
        runtimePlugin: z.object({
            pluginSlugs: z.array(z.literal("slack")).length(1),
            secretName: z.literal("SLACK_MCP_OAUTH_TOKEN"),
            value: z.string().min(1),
            metadata: metadataSchema,
        }).strict().optional(),
        sourceConnector: z.object({
            organisationId: nonEmptyString,
            connectorProvider: z.literal("slack"),
            enabledEntities: z.array(z.enum(["mention", "direct_message"])),
            config: metadataSchema,
            secrets: z.object({ SLACK_MCP_OAUTH_TOKEN: z.string().min(1) }).strict(),
        }).strict().optional(),
        outboundSlackEndpoint: slackOutboundEndpointSchema.optional(),
    }).strict(),
    z.object({
        providerSlug: nonEmptyString,
        kind: z.literal("plugin-secret"),
        pluginSlugs: z.array(nonEmptyString).min(1),
        secretName: nonEmptyString,
        value: z.string().min(1),
        metadata: metadataSchema,
        sourceConnector: sourceConnectorSchema.optional(),
        sourceConnectors: z.array(googleSourceConnectorSchema).min(1).optional(),
        outboundSlackEndpoint: slackOutboundEndpointSchema.optional(),
    }).strict(),
    z.object({
        providerSlug: nonEmptyString,
        kind: z.literal("connector"),
        organisationId: nonEmptyString,
        connectorProvider: z.enum([
            "xero", "stripe", "sharepoint", "github", "hubspot", "slack",
        ]),
        config: metadataSchema,
        secrets: secretValuesSchema,
    }).strict(),
    z.object({
        // An admin signed in as an organisation mailbox (info@): sync only, no live-tool grant.
        providerSlug: z.literal("google-workspace"),
        kind: z.literal("shared-mailbox"),
        sourceConnector: googleSourceConnectorSchema.extend({ connectorProvider: z.literal("gmail") }).strict(),
    }).strict(),
    z.object({
        providerSlug: z.literal("microsoft-teams"),
        kind: z.literal("external-user-link"),
        externalTenantId: nonEmptyString.max(256),
        externalUserId: nonEmptyString.max(256),
        externalDisplayName: nonEmptyString.max(256).optional(),
        externalEmail: nonEmptyString.max(320).optional(),
        metadata: metadataSchema,
        pluginSecret: z.object({
            pluginSlugs: z.array(z.literal("microsoft-teams")).min(1),
            secretName: z.literal("MICROSOFT_TEAMS_OAUTH_TOKEN"),
            value: z.string().min(1),
            metadata: metadataSchema,
        }).strict().optional(),
    }).strict(),
]);
const outboundEndpointReceiptSchema = z.object({
    client: z.literal("slack"),
    endpointId: nonEmptyString,
}).strict();
export const nativeOAuthHandoffResponseSchema = z.union([
    connectorIntegrationSchema,
    z.object({
        ok: z.literal(true),
        providerSlug: z.literal("microsoft-teams"),
        kind: z.literal("external-user-link"),
        created: z.boolean(),
        pluginSecretConfigured: z.boolean(),
    }).strict(),
    z.object({
        ok: z.literal(true),
        providerSlug: z.literal("slack"),
        kind: z.literal("slack-capabilities"),
        capabilities: z.array(slackConnectionCapabilitySchema),
        runtimeConfigured: z.boolean(),
        sourceConnector: connectorIntegrationSchema.optional(),
        outboundEndpoint: outboundEndpointReceiptSchema.optional(),
    }).strict(),
    z.object({
        ok: z.literal(true),
        providerSlug: nonEmptyString,
        kind: z.literal("plugin-secret"),
        pluginSlugs: z.array(nonEmptyString).min(1),
        sourceConnector: connectorIntegrationSchema.optional(),
        sourceConnectors: z.array(connectorIntegrationSchema),
        outboundEndpoint: outboundEndpointReceiptSchema.optional(),
    }).strict(),
]);
